import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import {
  Bell,
  LayoutDashboard,
  LogOut,
  Palette,
  Search,
  Settings,
  Shield,
  UserCircle,
} from 'lucide-react';
import type { Permission } from '@taskflow/shared';
import {
  Avatar,
  CountBadge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Input,
  ThemeToggle,
  Tooltip,
  cn,
} from '@taskflow/ui';
import { Logo } from '../components/brand/Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth, useCurrentUser } from '../features/auth/AuthContext';
import { usePermissions } from '../features/auth/usePermissions';
import { en } from '../locales/en';

/**
 * The authenticated application frame.
 *
 * Landmarks (`header`, `nav`, `main`) are real elements, not styled divs, because
 * they are what lets a screen-reader user jump straight to the content instead of
 * tabbing through the whole navigation on every page.
 *
 * Navigation entries are filtered by permission — a Member never sees an Admin link
 * they cannot use. That is a courtesy, not security: the route guard refuses the
 * page and the Worker refuses the API call regardless.
 */

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** When set, the entry is hidden unless the user holds this permission. */
  permission?: Permission;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/boards', label: en.nav.boards, icon: LayoutDashboard },
  { to: '/search', label: en.nav.search, icon: Search },
  { to: '/admin', label: en.nav.admin, icon: Shield, permission: 'user.list' },
  { to: '/settings', label: en.nav.settings, icon: Settings },
  { to: '/ui-kit', label: en.nav.uiKit, icon: Palette },
];

export function AppShell() {
  const { theme, setTheme } = useTheme();
  const { logout } = useAuth();
  const user = useCurrentUser();
  const { can } = usePermissions();
  const navigate = useNavigate();

  const visibleItems = NAV_ITEMS.filter((item) => !item.permission || can(item.permission));

  async function handleSignOut(): Promise<void> {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-surface-base">
      {/*
        Skip link. Visually hidden until focused, so the first Tab on any page offers a
        jump straight to the content — without it a keyboard user tabs through the whole
        navigation on every single page, which is the most common keyboard-accessibility
        complaint about apps with a persistent sidebar.
      */}
      <a
        href="#main-content"
        className="sr-only-focusable absolute left-2 top-2 z-[--z-toast] rounded-sm bg-surface-overlay px-3 py-2 text-sm font-medium text-text-primary shadow-e3"
      >
        Skip to content
      </a>

      <nav
        aria-label="Main navigation"
        className="flex w-16 flex-col gap-1 border-r border-border-subtle bg-surface-raised p-2 lg:w-56 lg:p-3"
      >
        <div className="mb-3 flex h-9 items-center px-1">
          <Logo size="sm" showWordmark={false} className="lg:hidden" />
          <Logo size="sm" className="hidden lg:inline-flex" />
        </div>

        {visibleItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-sm px-2.5 py-2 text-sm font-medium',
                  'transition-colors duration-[--duration-micro]',
                  isActive
                    ? 'bg-primary-subtle text-text-link'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                )
              }
            >
              <Icon className="size-4 shrink-0" aria-hidden="true" />
              <span className="hidden lg:inline">{item.label}</span>
              {/* On the collapsed rail the label is the only accessible name, so it
                  stays in the DOM for assistive technology. */}
              <span className="sr-only lg:hidden">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border-subtle bg-surface-raised px-4">
          <TopBarSearch onSubmit={(q) => navigate(`/search?q=${encodeURIComponent(q)}`)} />

          <ThemeToggle value={theme} onChange={setTheme} />

          <Tooltip content={en.nav.notifications}>
            <span className="relative inline-flex">
              <IconButton label={en.nav.notifications} icon={<Bell aria-hidden="true" />} />
              <CountBadge count={0} className="absolute -right-0.5 -top-0.5" />
            </span>
          </Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-full"
                // The name is the accessible label: "user menu" alone would not tell
                // a screen-reader user which account they are signed in as.
                aria-label={`Account menu for ${user.name}`}
              >
                <Avatar name={user.name} colourKey={user.id} src={user.avatarUrl} size="md" />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
              <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
              <div className="px-2 pb-1.5 text-2xs text-text-tertiary">{user.email}</div>
              <DropdownMenuSeparator />

              <DropdownMenuItem onSelect={() => navigate('/settings')}>
                <UserCircle aria-hidden="true" /> {en.nav.settings}
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onSelect={() => void handleSignOut()}>
                <LogOut aria-hidden="true" /> {en.common.signOut}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* `tabIndex={-1}` so the skip link can actually focus it — an anchor target that
            is not focusable moves the viewport but leaves focus behind in the nav. */}
        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The header search box. Submitting jumps to the full search page with the query in the
 * URL, where the filters and live results live — the box itself stays a simple launcher.
 */
function TopBarSearch({ onSubmit }: { onSubmit: (query: string) => void }) {
  const [value, setValue] = useState('');

  return (
    <form
      role="search"
      className="relative mr-auto w-full max-w-xs"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = value.trim();
        if (trimmed.length > 0) onSubmit(trimmed);
      }}
    >
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-tertiary"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={en.nav.search}
        aria-label="Search cards"
        className="h-9 pl-9"
      />
    </form>
  );
}
